import { Node, mergeAttributes } from '@tiptap/core'

export const JOHKU_EMBED_CLASS = 'johku-embed'
export const JOHKU_EMBED_SANDBOX =
  'allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-same-origin'

function parseClassList(value: string | null): string[] {
  return (value || '')
    .split(/\s+/)
    .map(part => part.trim())
    .filter(Boolean)
}

function withJohkuEmbedClass(value: string | null | undefined): string {
  const classes = parseClassList(value || '')
  if (!classes.includes(JOHKU_EMBED_CLASS)) {
    classes.push(JOHKU_EMBED_CLASS)
  }
  return classes.join(' ')
}

const Iframe = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  draggable: true,

  addOptions() {
    return {
      allowFullscreen: true,
      HTMLAttributes: {
        class: 'iframe-wrapper',
      },
    }
  },

  addAttributes() {
    return {
      src: {
        default: null,
      },
      srcdoc: {
        default: null,
      },
      sandbox: {
        default: JOHKU_EMBED_SANDBOX,
      },
      class: {
        default: JOHKU_EMBED_CLASS,
        parseHTML: (element: HTMLElement) => withJohkuEmbedClass(element.getAttribute('class')),
        renderHTML: (attributes: Record<string, unknown>) => ({
          class: withJohkuEmbedClass(attributes.class as string | null | undefined),
        }),
      },
      frameborder: {
        default: 0,
      },
      allowfullscreen: {
        default: this.options.allowFullscreen,
        parseHTML: () => this.options.allowFullscreen,
      },
      allow: {
        default: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen',
        parseHTML: (element: HTMLElement) => {
          const value = element.getAttribute('allow') || ''
          return value.includes('fullscreen')
            ? value
            : (value ? `${value}; fullscreen` : 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share; fullscreen')
        },
      },
      width: {
        default: '100%',
        renderHTML: attributes => attributes.width
          ? { width: attributes.width === '100%' ? '100%' : parseInt(attributes.width as string, 10) }
          : { width: '100%' },
        parseHTML: element => element.getAttribute('width') || '100%',
      },
      height: {
        default: 400,
        renderHTML: attributes => attributes.height
          ? { height: parseInt(String(attributes.height), 10) }
          : { height: 400 },
        parseHTML: element => {
          const height = element.getAttribute('height')
          return height ? parseInt(height, 10) : 400
        },
      },
      style: {
        default: null,
        renderHTML: attributes => attributes.style
          ? { style: attributes.style }
          : {},
        parseHTML: element => element.getAttribute('style'),
      },
    }
  },

  parseHTML() {
    return [{ tag: 'iframe' }]
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = { ...HTMLAttributes }
    if (attrs.srcdoc) {
      delete attrs.src
    }
    return ['div', this.options.HTMLAttributes, ['iframe', mergeAttributes(attrs)]]
  },

  addNodeView() {
    return ({ node }) => {
      const dom = document.createElement('div')
      dom.className = 'iframe-wrapper johku-embed-placeholder'
      dom.setAttribute('data-drag-handle', '')
      dom.contentEditable = 'false'

      const icon = document.createElement('span')
      icon.className = 'fa fa-fw fa-puzzle-piece johku-embed-placeholder__icon'
      icon.setAttribute('aria-hidden', 'true')

      const label = document.createElement('div')
      label.className = 'johku-embed-placeholder__label'
      label.textContent = 'Interactive embed'

      const detail = document.createElement('div')
      detail.className = 'johku-embed-placeholder__detail'
      if (node.attrs.srcdoc) {
        detail.textContent = 'HTML app'
      } else if (node.attrs.src) {
        detail.textContent = String(node.attrs.src)
      } else {
        detail.textContent = 'Empty embed'
      }

      const height = parseInt(String(node.attrs.height || 400), 10)
      dom.style.minHeight = `${Math.min(Math.max(height, 120), 240)}px`

      dom.appendChild(icon)
      dom.appendChild(label)
      dom.appendChild(detail)

      return {
        dom,
        ignoreMutation: () => true,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'iframe') {
            return false
          }
          detail.textContent = updatedNode.attrs.srcdoc
            ? 'HTML app'
            : (updatedNode.attrs.src ? String(updatedNode.attrs.src) : 'Empty embed')
          const nextHeight = parseInt(String(updatedNode.attrs.height || 400), 10)
          dom.style.minHeight = `${Math.min(Math.max(nextHeight, 120), 240)}px`
          return true
        },
      }
    }
  },

  addCommands() {
    return {
      setIframe: (attributes: Record<string, unknown> = {}) => ({ tr, dispatch }) => {
        const { selection } = tr
        const node = this.type.create({
          sandbox: JOHKU_EMBED_SANDBOX,
          width: '100%',
          height: 400,
          ...attributes,
          class: withJohkuEmbedClass(attributes.class as string | null | undefined),
        })

        if (dispatch) {
          tr.replaceRangeWith(selection.from, selection.to, node)
        }

        return true
      },
    }
  },
})

export default Iframe
